import React, { useState, useEffect, useMemo } from 'react';
import {
    Users,
    Search,
    Maximize2
} from 'lucide-react';
import { FaClipboardCheck } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';
import TeamAttendanceHistoryModal from './TeamAttendanceHistoryModal';
import './CoordinatorAttendanceManager.css';

const CoordinatorAttendanceManager = ({ coordinatorSportId = null }) => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [selectedTeamDetails, setSelectedTeamDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [seasonFilter, setSeasonFilter] = useState('all');
    const [isFullScreenModalOpen, setIsFullScreenModalOpen] = useState(false);

    // Fetch teams for coordinator
    useEffect(() => {
        const fetchTeams = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/api/teams`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    let filtered = data;
                    if (coordinatorSportId) {
                        filtered = filtered.filter(t => t.sportId === parseInt(coordinatorSportId));
                    }
                    // Sort active first, then by name
                    filtered.sort((a, b) => {
                        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                        return (a.name || '').localeCompare(b.name || '');
                    });
                    setTeams(filtered);
                    if (filtered.length > 0) {
                        setSelectedTeamId(filtered[0].id);
                    }
                }
            } catch (err) {
                console.error('Error fetching coordinator teams:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchTeams();
    }, [coordinatorSportId]);

    // Fetch details of selected team (roster of athletes)
    useEffect(() => {
        if (!selectedTeamId) {
            setSelectedTeamDetails(null);
            return;
        }

        const fetchDetails = async () => {
            setDetailsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/api/teams/${selectedTeamId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setSelectedTeamDetails(data);
                } else {
                    const fallback = teams.find(t => t.id === selectedTeamId);
                    setSelectedTeamDetails(fallback);
                }
            } catch (err) {
                console.error('Error fetching team details:', err);
                const fallback = teams.find(t => t.id === selectedTeamId);
                setSelectedTeamDetails(fallback);
            } finally {
                setDetailsLoading(false);
            }
        };

        fetchDetails();
    }, [selectedTeamId, teams]);

    // Available seasons for filtering
    const availableSeasons = useMemo(() => {
        const set = new Set();
        teams.forEach(t => {
            if (t.season) set.add(t.season);
        });
        return Array.from(set).sort().reverse();
    }, [teams]);

    // Filtered teams list
    const filteredTeams = useMemo(() => {
        return teams.filter(t => {
            if (seasonFilter !== 'all' && t.season !== seasonFilter) return false;
            if (searchTerm.trim()) {
                const q = searchTerm.toLowerCase();
                const matchName = t.name?.toLowerCase().includes(q);
                const matchCat = t.category?.toLowerCase().includes(q);
                return matchName || matchCat;
            }
            return true;
        });
    }, [teams, seasonFilter, searchTerm]);

    const activeTeamObj = teams.find(t => t.id === selectedTeamId);

    if (loading) {
        return (
            <div className="coord-attendance-loading">
                <div className="spinner"></div>
                <p>A carregar equipas da modalidade...</p>
            </div>
        );
    }

    if (teams.length === 0) {
        return (
            <div className="coord-attendance-empty">
                <Users size={48} className="empty-icon" />
                <h3>Sem equipas disponíveis</h3>
                <p>Não foram encontradas equipas associadas à sua coordenação.</p>
            </div>
        );
    }

    return (
        <div className="coord-attendance-wrapper">
            {/* Top Selector Card */}
            <div className="coord-team-selector-card">
                <div className="selector-header">
                    <div className="selector-title">
                        <FaClipboardCheck className="title-icon" />
                        <div>
                            <h3>Controlo e Histórico de Presenças</h3>
                            <p>Selecione uma equipa para analisar a assiduidade e exportar para Excel</p>
                        </div>
                    </div>

                    <div className="selector-filters">
                        {availableSeasons.length > 1 && (
                            <select
                                className="season-select"
                                value={seasonFilter}
                                onChange={(e) => setSeasonFilter(e.target.value)}
                            >
                                <option value="all">Todas as Épocas</option>
                                {availableSeasons.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        )}

                        <div className="search-wrap">
                            <Search size={16} />
                            <input
                                type="text"
                                placeholder="Pesquisar equipa..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Team Pills Carousel */}
                <div className="teams-pills-row">
                    {filteredTeams.map(team => {
                        const isSelected = team.id === selectedTeamId;
                        return (
                            <button
                                key={team.id}
                                className={`team-select-pill ${isSelected ? 'active' : ''}`}
                                onClick={() => setSelectedTeamId(team.id)}
                            >
                                <span className="pill-name">{team.name}</span>
                                {team.category && <span className="pill-category">{team.category}</span>}
                                {team.season && <span className="pill-season">{team.season}</span>}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Board Section */}
            {detailsLoading ? (
                <div className="coord-attendance-loading">
                    <div className="spinner"></div>
                    <p>A carregar quadro de assiduidade de {activeTeamObj?.name}...</p>
                </div>
            ) : selectedTeamDetails ? (
                <div className="coord-board-container">
                    <div className="coord-board-actions-bar">
                        <div className="active-team-badge-info">
                            <h4>{selectedTeamDetails.name}</h4>
                            <span>{selectedTeamDetails.sportName} • {selectedTeamDetails.category} • {selectedTeamDetails.season}</span>
                        </div>

                        <button
                            className="btn-open-fullscreen"
                            onClick={() => setIsFullScreenModalOpen(true)}
                            title="Expandir quadro em ecrã inteiro"
                        >
                            <Maximize2 size={16} />
                            <span>Ecrã Inteiro</span>
                        </button>
                    </div>

                    {/* Embedded Board */}
                    <TeamAttendanceHistoryModal
                        embedded={true}
                        readOnly={true}
                        teamId={selectedTeamDetails.id}
                        athletes={selectedTeamDetails.athletes || []}
                        teamName={selectedTeamDetails.name}
                        sportName={selectedTeamDetails.sportName}
                        season={selectedTeamDetails.season}
                    />

                    {/* Full screen modal */}
                    <TeamAttendanceHistoryModal
                        isOpen={isFullScreenModalOpen}
                        onClose={() => setIsFullScreenModalOpen(false)}
                        readOnly={true}
                        teamId={selectedTeamDetails.id}
                        athletes={selectedTeamDetails.athletes || []}
                        teamName={selectedTeamDetails.name}
                        sportName={selectedTeamDetails.sportName}
                        season={selectedTeamDetails.season}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default CoordinatorAttendanceManager;
